import { motion } from "framer-motion";
import { CHANNELS, USER_ASSETS } from "@/lib/monuments";
import {
  Glasses,
  Smartphone,
  Globe2,
  GraduationCap,
  BookOpen,
  Building2,
} from "lucide-react";

const ICONS = [Glasses, Smartphone, Globe2, GraduationCap, BookOpen, Building2];
const EASE = [0.22, 1, 0.36, 1];

// Subtle monument-photo backgrounds for the two hero tiles (from user's assets)
const TILE_BG = [USER_ASSETS.splitTemple, USER_ASSETS.panoramic];

export default function Omnichannel() {
  return (
    <section
      id="platform"
      data-testid="omnichannel-section"
      className="relative py-20 md:py-28 border-t border-gold/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12 md:mb-14">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 font-mono-accent text-[11px] text-gold/80">
              <span>Chapter 03</span>
              <span className="w-8 h-px bg-gold/40" />
              <span className="text-bone/50">The Platform</span>
            </div>
            <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] text-bone tracking-tight">
              One heritage core.
              <br />
              <em className="text-gold not-italic">Six ways in.</em>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-bone/70 text-lg leading-relaxed">
              An omnichannel delivery lattice — from the physical site to the
              classroom, from the phone in a tourist&rsquo;s hand to the museum
              wing in a capital city.
            </p>
          </div>
        </div>

        {/* Bento grid — first two hero tiles use user-provided monument imagery */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          {CHANNELS.map((c, i) => {
            const Icon = ICONS[i];
            const isHero = i < 2;
            const spanClass = "md:col-span-3";
            const minH = isHero
              ? "min-h-[360px] md:min-h-[440px]"
              : "min-h-[260px] md:min-h-[300px]";
            return (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, ease: EASE, delay: i * 0.05 }}
                className={`lift relative bg-surface border border-gold/12 p-7 md:p-9 overflow-hidden group ${spanClass} ${minH}`}
                data-testid={`channel-tile-${c.n}`}
              >
                {/* Hero-tile monument background image */}
                {isHero && (
                  <>
                    <div
                      className="absolute inset-0 bg-cover bg-center opacity-25 group-hover:opacity-35 transition-opacity duration-700"
                      style={{ backgroundImage: `url(${TILE_BG[i]})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/85 to-obsidian/40" />
                    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/6 blur-3xl pointer-events-none" />
                  </>
                )}

                <div className="relative z-10 h-full flex flex-col justify-between gap-6">
                  <div className="flex items-start justify-between">
                    <span className="font-mono-accent text-[10px] text-gold/80">
                      {c.n} — {c.accent}
                    </span>
                    <Icon className="w-5 h-5 text-gold/70" strokeWidth={1.2} />
                  </div>
                  <div>
                    <h3
                      className={`font-display leading-[1.05] text-bone ${
                        isHero ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"
                      }`}
                    >
                      {c.title}
                    </h3>
                    <p className="mt-4 text-bone/70 leading-relaxed text-sm md:text-base">
                      {c.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
